/**
 * OpenRedirectDetector — Detection of open redirect attempt indicators
 * in client-side outbound requests and page load URLs.
 *
 * Detects redirect/navigation parameters whose values reference:
 *   1. javascript: scheme — always dangerous; no legitimate use in redirect params.
 *   2. data: scheme — can execute scripts in redirect contexts.
 *   3. Protocol-relative URLs (//evil.com) — no legitimate reason to use in redirect params.
 *   4. Absolute URLs pointing to a different origin — potential phishing redirect (medium confidence).
 *   5. URL-encoded forms of the above — WAF bypass attempt (high confidence).
 *
 * Only inspects parameters whose names are commonly used to carry redirect targets.
 * Does NOT block requests; only returns a detection result.
 *
 * CWE-601 | OWASP A01:2025 – Broken Access Control
 * MITRE: T1566.002 (Spearphishing Link — attacker-crafted redirect URL)
 */

/**
 * Parameter names that commonly carry a redirect/navigation target URL.
 * Only values of these parameters are evaluated for redirect risk.
 */
const REDIRECT_PARAM_NAMES = /^(redirect|redirect_url|redirect_to|redirecturl|redirecturi|return|return_url|return_to|returnurl|returnuri|next|url|goto|back|to|continue|success_url|cancel_url|callback|callback_url|forward|fwd|destination|dest|after_login|after_logout|login_redirect|logout_redirect)$/i;

/** javascript: scheme in a redirect target is never legitimate. */
const JAVASCRIPT_SCHEME = /^javascript\s*:/i;

/** data: scheme can execute scripts via data:text/html or similar MIME types. */
const DATA_SCHEME = /^data\s*:/i;

/**
 * Protocol-relative URL: starts with //.
 * No legitimate redirect sends users to //host — relative redirects use /path,
 * not //host/path. This is almost always an attacker-controlled external domain.
 */
const PROTOCOL_RELATIVE = /^\/\//;

export class OpenRedirectDetector {
	/**
	 * Analyzes a URL for open redirect indicators by scanning its query parameters.
	 *
	 * @param {string} url           — The URL to analyze (page URL or fetch/XHR destination).
	 * @param {string} currentOrigin — window.location.origin of the current page,
	 *                                  used for cross-origin comparison. Pass '' to skip
	 *                                  absolute_redirect checks.
	 * @returns {{ detected: true,  type: 'OPEN_REDIRECT', variant: string,
	 *             evidence: string, confidence: 'high'|'medium' }
	 *          | { detected: false }}
	 */
	analyze(url, currentOrigin) {
		if (!url || typeof url !== 'string') return { detected: false };

		const hit = this._scanQueryParams(url, currentOrigin || '');
		if (hit) {
			return {
				detected: true,
				type: 'OPEN_REDIRECT',
				variant: hit.variant,
				evidence: hit.evidence,
				confidence: hit.confidence
			};
		}

		return { detected: false };
	}

	/**
	 * Scans the query string of a URL for redirect parameters with dangerous values.
	 * @param {string} url
	 * @param {string} currentOrigin
	 * @returns {{ variant: string, evidence: string, confidence: 'high'|'medium' }|null}
	 */
	_scanQueryParams(url, currentOrigin) {
		let search;
		try {
			search = new URL(url).search;
		} catch (_) {
			// Not a parseable absolute URL — try extracting query string manually.
			const idx = url.indexOf('?');
			if (idx === -1) return null;
			search = url.substring(idx);
		}
		if (!search || search === '?') return null;

		let params;
		try {
			params = new URLSearchParams(search);
		} catch (_) {
			return null;
		}

		for (const [name, value] of params.entries()) {
			if (!REDIRECT_PARAM_NAMES.test(name)) continue;
			if (!value || value.length < 2) continue;

			// 1. Check the raw value
			const rawHit = this._classifyValue(value, currentOrigin);
			if (rawHit) return rawHit;

			// 2. Check the URL-decoded value — catches encoded bypass attempts
			const decoded = this._safeDecode(value);
			if (decoded) {
				const decodedHit = this._classifyValue(decoded, currentOrigin);
				if (decodedHit) {
					// Re-encoding dangerous values is always an intentional bypass.
					return {
						variant: 'encoded_redirect',
						evidence: value.substring(0, 64),
						confidence: 'high'
					};
				}
			}
		}

		return null;
	}

	/**
	 * Classifies a single parameter value as a redirect risk, if applicable.
	 * @param {string} value
	 * @param {string} currentOrigin
	 * @returns {{ variant: string, evidence: string, confidence: 'high'|'medium' }|null}
	 */
	_classifyValue(value, currentOrigin) {
		// CRLF injection in redirect value — %0d%0a or literal \r\n enables HTTP response
		// splitting via the Location header, allowing header injection and phishing redirects
		if (/[\r\n]/.test(value) || /%0[aAdD]/i.test(value)) {
			return {
				variant: 'newline_header_redirect',
				evidence: value.substring(0, 64),
				confidence: 'high'
			};
		}

		// javascript: scheme — script execution via redirect
		if (JAVASCRIPT_SCHEME.test(value)) {
			return {
				variant: 'javascript_redirect',
				evidence: value.substring(0, 64),
				confidence: 'high'
			};
		}

		// data: scheme — can encode HTML/script payloads
		if (DATA_SCHEME.test(value)) {
			return {
				variant: 'data_redirect',
				evidence: value.substring(0, 64),
				confidence: 'high'
			};
		}

		// Backslash protocol-relative: \//evil.com or \evil.com
		// Browsers normalize a leading backslash to forward slash in redirect contexts,
		// so \//evil.com resolves identically to //evil.com — deliberate filter bypass.
		if (/^\\/.test(value)) {
			return {
				variant: 'protocol_relative_redirect',
				evidence: value.substring(0, 64),
				confidence: 'high'
			};
		}

		// Protocol-relative: //hostname/path
		if (PROTOCOL_RELATIVE.test(value)) {
			return {
				variant: 'protocol_relative_redirect',
				evidence: value.substring(0, 64),
				confidence: 'high'
			};
		}

		// Absolute URL to a different origin
		if (/^https?:\/\//i.test(value) && currentOrigin) {
			try {
				const parsed = new URL(value);
				if (parsed.origin !== currentOrigin) {
					return {
						variant: 'absolute_redirect',
						evidence: parsed.hostname,
						confidence: 'medium'
					};
				}
			} catch (_) {
				// Cannot parse — skip
			}
		}

		return null;
	}

	/**
	 * Attempts a single URL-decode pass. Returns null if no % sequences present
	 * or decoding produces the same value (nothing to decode).
	 * @param {string} value
	 * @returns {string|null}
	 */
	_safeDecode(value) {
		if (!value || value.indexOf('%') === -1) return null;
		try {
			const decoded = decodeURIComponent(value.replace(/\+/g, ' '));
			return decoded !== value ? decoded : null;
		} catch (_) {
			return null;
		}
	}
}

export default OpenRedirectDetector;
